import { Injectable } from '@angular/core';
import { SocketIOEventEnum } from '@zaoblako/shared/common/enums/socketIO/socket.io.event.enum';
import { SocketIONamespaceMap } from '@zaoblako/shared/common/enums/socketIO/socket.io.namespace.map';
import { SocketIOEventTypeEnum } from '@zaoblako/shared/common/enums/socketIO/socket.io.event.type.enum';
import type { SocketIOMessageInterface } from '@zaoblako/shared/common/interfaces/socket.io.message.interface';
import type { NotificationFindInterface } from '@zaoblako/shared/modules/notification/dtos/notification-find.dto';
import type { ActivityFeedInformerQueryInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-query.dto';
import type { NotificationMarkManyAsReadQueryInterface } from '@zaoblako/shared/modules/notification/dtos/notification-mark-many-as-read-query.dto';
import type { ActivityFeedInformerPageFindInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-page-find.dto';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { MessageService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

@Injectable({
  providedIn: 'root'
})
export class WebsocketNotificationService {
  public socket: Socket;
  public message$: Subject<SocketIOMessageInterface<NotificationFindInterface>> = new Subject();
  public socketNotificationRead$: Subject<NotificationFindInterface> = new Subject();

  public constructor(private readonly messageService: MessageService) {}

  public createWSConnection(authToken: string, userId: number, isExternal?: boolean) {
    const ns = SocketIONamespaceMap[isExternal ? SocketIOEventTypeEnum.EXTERNAL_NOTIFICATION : SocketIOEventTypeEnum.NOTIFICATION];
    this.socket = io(ns, {
      path: '/api/socket/connect',
      timeout: 10 * 60 * 1000,
      auth: {
        token: authToken
      },
      withCredentials: true,
      query: {
        roomName: userId
      },
      multiplex: false
    });

    this.socket.on(SocketIOEventEnum.DISCONNECT, (reason) => {
      console.error('ws disconnect', reason);
      if (reason === 'io server disconnect') {
        this.socket.connect();
      }
    });

    this.socket.on(SocketIOEventEnum.NOTIFICATION_NEW, (message) => {
      this.message$.next(message);
    });
  }

  public findList(payload: SocketIOMessageInterface<ActivityFeedInformerQueryInterface>): Observable<ActivityFeedInformerPageFindInterface> {
    return this.emitAsObservable(SocketIOEventEnum.NOTIFICATION_LIST, payload);
  }

  public readNotification(payload: SocketIOMessageInterface<NotificationMarkManyAsReadQueryInterface>): Observable<null> {
    return this.emitAsObservable(SocketIOEventEnum.NOTIFICATION_READ, payload);
  }

  public disconnectWS() {
    if (this.socket?.connected) {
      this.socket.disconnect();
    }
  }

  public getNewMessage(): Observable<SocketIOMessageInterface<NotificationFindInterface>> {
    return this.message$.asObservable();
  }

  public showNotificationPopup(notification: NotificationFindInterface) {
    this.messageService.add({ key: 'notification', severity: 'notification', data: notification, life: 10000 });
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        this.sendBrowserNotification(notification);
      } else if (Notification.permission === 'denied' || Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            this.sendBrowserNotification(notification);
          }
        });
      }
    }
  }

  private emitAsObservable<T>(event: SocketIOEventEnum, payload: any): Observable<T> {
    return new Observable((subscriber) => {
      this.socket.emit(event, payload, (response: { success: boolean; error: string; result: any }) => {
        if (response.success) {
          subscriber.next(response.result);
          subscriber.complete();
        }
        subscriber.error(response.error);
      });
    });
  }

  private sendBrowserNotification(notification: NotificationFindInterface) {
    if (notification.kind === NotificationKindEnum.PERSONAL) {
      setTimeout(() => {
        new Notification(`${notification.subject?.title}. Вас упомянули`, {
          body: notification?.user?.profile?.name || notification?.externalUser?.profile?.name || 'Сервисное уведомление',
          icon: '/assets/images/favicon.png'
        });
      }, 2000);
    }
  }
}
